import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';

const HarmonyLitepaper = () => {
  return (
    <div className="min-h-screen bg-black text-white">
      <Helmet>
        <title>Harmony Signal Engine Litepaper | SELF Labs</title>
        <meta name="description" content="Sovereign AI Execution Through Embedded Signal Metadata - The technical foundation of the Signal Economy." />
      </Helmet>

      <div className="container mx-auto px-4 py-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="max-w-4xl mx-auto"
        >
          {/* Header */}
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-5xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-indigo-400 via-purple-400 to-blue-400">
              Harmony Signal Engine Litepaper
            </h1>
            <p className="text-xl text-gray-300 mb-4">Sovereign AI Execution Through Embedded Signal Metadata</p>
            <p className="text-gray-400">Invented by Milan Cheeks, Founder and CEO of SELF Labs Inc.</p>
          </div>

          {/* Content Sections */}
          <div className="space-y-16">
            {/* Introduction */}
            <section>
              <h2 className="text-2xl font-bold mb-6 flex items-center text-indigo-400">
                <span className="mr-2">📡</span> Introduction
              </h2>
              <div className="prose prose-invert max-w-none">
                <p className="text-gray-300">
                  The future of digital sovereignty belongs to those who control their communications, AI operations, and economic participation.
                  Harmony unlocks this future — embedding executable metadata into media streams to create autonomous AI-driven execution across decentralized networks.
                </p>
                <p className="text-gray-300">
                  The SELF Phone, powered by the Harmony Signal Engine, activates a new era:
                  The Signal Economy — where every action, every execution, and every reward belongs directly to the user.
                </p>
              </div>
            </section>

            {/* The Problem */}
            <section>
              <h2 className="text-2xl font-bold mb-6 flex items-center text-indigo-400">
                <span className="mr-2">🧠</span> The Problem
              </h2>
              <div className="prose prose-invert max-w-none">
                <p className="text-gray-300">Today's AI systems are centralized, heavily surveilled, and serve primarily corporate owners — not users.</p>
                <p className="text-gray-300">Centralized cloud servers:</p>
                <ul className="list-disc pl-6 text-gray-300">
                  <li>Require constant internet access.</li>
                  <li>Introduce security vulnerabilities.</li>
                  <li>Prevent users from earning sovereign rewards for their contributions to AI operations.</li>
                </ul>
                <p className="text-gray-300">There has been no system where individuals could autonomously participate in AI task execution — until now.</p>
              </div>
            </section>

            {/* The Solution */}
            <section>
              <h2 className="text-2xl font-bold mb-6 flex items-center text-indigo-400">
                <span className="mr-2">🌐</span> The Solution: Harmony Signal Engine
              </h2>
              <div className="prose prose-invert max-w-none">
                <p className="text-gray-300">
                  Harmony transforms passive media (music, video, ambient sound) into an active command layer.
                  Through embedded encrypted metadata signals, Harmony Agents perform real-world tasks, triggered through everyday media experiences — even without continuous internet reliance.
                </p>
                <p className="text-gray-300">
                  By participating, users earn ESSENCE: a sovereign utility reward token reflecting the successful autonomous execution of AI tasks across the decentralized Harmony Mesh.
                </p>
              </div>
            </section>

            {/* How Harmony Works */}
            <section>
              <h2 className="text-2xl font-bold mb-6 flex items-center text-indigo-400">
                <span className="mr-2">🛰</span> How Harmony Works
              </h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-indigo-950/30 p-6 rounded-xl">
                  <h3 className="font-semibold mb-3">Signal Embedding</h3>
                  <p className="text-gray-300">Harmony-enhanced media carries encrypted metadata inside its audio or video streams.</p>
                </div>
                <div className="bg-indigo-950/30 p-6 rounded-xl">
                  <h3 className="font-semibold mb-3">Signal Reception</h3>
                  <p className="text-gray-300">SELF Phone users receive media signals through local mesh, satellite links, or ambient capture.</p>
                </div>
                <div className="bg-indigo-950/30 p-6 rounded-xl">
                  <h3 className="font-semibold mb-3">Signal Decryption</h3>
                  <p className="text-gray-300">Harmony OS decrypts and authenticates incoming signals using OmniSeal™ zero-knowledge encryption.</p>
                </div>
                <div className="bg-indigo-950/30 p-6 rounded-xl">
                  <h3 className="font-semibold mb-3">Agent Activation</h3>
                  <p className="text-gray-300">Harmony AI Agents autonomously execute the tasks attached to the validated signal.</p>
                </div>
              </div>
            </section>

            {/* Harmony AI Agent Framework */}
            <section>
              <h2 className="text-2xl font-bold mb-6 flex items-center text-indigo-400">
                <span className="mr-2">🤖</span> Harmony AI Agent Framework
              </h2>
              <p className="text-gray-300 mb-6">Each SELF Phone hosts a decentralized fleet of Harmony Agents, specialized across enterprise domains:</p>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-indigo-950/30 p-6 rounded-xl">
                  <h3 className="font-semibold mb-3">CTO Suite</h3>
                  <ul className="list-disc pl-6 text-gray-300">
                    <li>System Monitor</li>
                    <li>Cybersecurity Auditor</li>
                    <li>Deployment Coordinator</li>
                  </ul>
                </div>
                <div className="bg-indigo-950/30 p-6 rounded-xl">
                  <h3 className="font-semibold mb-3">CMO Suite</h3>
                  <ul className="list-disc pl-6 text-gray-300">
                    <li>Content Generator</li>
                    <li>Marketing Optimizer</li>
                    <li>Analytics Summarizer</li>
                  </ul>
                </div>
                <div className="bg-indigo-950/30 p-6 rounded-xl">
                  <h3 className="font-semibold mb-3">CFO Suite</h3>
                  <ul className="list-disc pl-6 text-gray-300">
                    <li>Ledger Validator</li>
                    <li>Invoice Reconciler</li>
                    <li>Micro-Auditor</li>
                  </ul>
                </div>
                <div className="bg-indigo-950/30 p-6 rounded-xl">
                  <h3 className="font-semibold mb-3">Legal & Operations</h3>
                  <ul className="list-disc pl-6 text-gray-300">
                    <li>Contract Template Composer</li>
                    <li>Terms of Service Generator</li>
                    <li>Workflow Architect</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Security and Sovereignty */}
            <section>
              <h2 className="text-2xl font-bold mb-6 flex items-center text-indigo-400">
                <span className="mr-2">🔒</span> Security and Sovereignty
              </h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-indigo-950/30 p-6 rounded-xl">
                  <h3 className="font-semibold mb-3">OmniSeal™ Encryption (PureCipher)</h3>
                  <ul className="list-disc pl-6 text-gray-300">
                    <li>Zero-knowledge encryption of signals and metadata at the hardware layer.</li>
                    <li>Prevents interception, alteration, or analysis of active transmissions.</li>
                  </ul>
                </div>
                <div className="bg-indigo-950/30 p-6 rounded-xl">
                  <h3 className="font-semibold mb-3">Rubidex™ Data Fabric Protection</h3>
                  <ul className="list-disc pl-6 text-gray-300">
                    <li>Immutable, blockchain-inspired storage for agent logs, task histories, and reward tracking.</li>
                    <li>Attribute-based access control ensures only authorized devices interact with protected records.</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* ESSENCE */}
            <section>
              <h2 className="text-2xl font-bold mb-6 flex items-center text-indigo-400">
                <span className="mr-2">🪙</span> ESSENCE — The Utility Reward Token
              </h2>
              <div className="prose prose-invert max-w-none">
                <p className="text-gray-300">
                  Participation in the Signal Economy is rewarded through ESSENCE — a stable-value utility token earned by users when their devices successfully complete Harmony-driven AI tasks.
                </p>
                <ul className="list-disc pl-6 text-gray-300">
                  <li>Not an investment vehicle — ESSENCE reflects active contribution, not speculative holding.</li>
                  <li>Utility-driven — Used to access premium AI agents, services, upgrades inside the Role Model AI App Store.</li>
                  <li>Optional liquidity — Where permitted, users may exchange ESSENCE for other digital assets or services.</li>
                  <li>User empowerment — ESSENCE belongs to the user, with no centralized dependency for participation or ownership.</li>
                </ul>
                <div className="bg-purple-900/30 p-6 rounded-xl mt-6">
                  <p className="text-sm text-gray-300 italic">
                    Legal Notice: ESSENCE is a utility token representing sovereign AI task execution rewards.
                    It does not constitute a security, investment contract, or promise of future returns.
                  </p>
                </div>
              </div>
            </section>

            {/* Real-World Examples */}
            <section>
              <h2 className="text-2xl font-bold mb-6 flex items-center text-indigo-400">
                <span className="mr-2">📈</span> Real-World Examples of Signal-Based Execution
              </h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-indigo-950/30 p-6 rounded-xl">
                  <h3 className="font-semibold mb-3">CTO Operations</h3>
                  <p className="text-gray-300">Autonomous infrastructure status reports and patch deployments.</p>
                </div>
                <div className="bg-indigo-950/30 p-6 rounded-xl">
                  <h3 className="font-semibold mb-3">CMO Marketing</h3>
                  <p className="text-gray-300">AI-generated campaign content, SEO reporting, trend analysis.</p>
                </div>
                <div className="bg-indigo-950/30 p-6 rounded-xl">
                  <h3 className="font-semibold mb-3">CFO Finance</h3>
                  <p className="text-gray-300">Ledger reconciliation and compliance verification generations.</p>
                </div>
                <div className="bg-indigo-950/30 p-6 rounded-xl">
                  <h3 className="font-semibold mb-3">Smart Infrastructure</h3>
                  <p className="text-gray-300">Ambient system monitoring for smart buildings and cities.</p>
                </div>
              </div>
            </section>

            {/* Signal Economy */}
            <section>
              <h2 className="text-2xl font-bold mb-6 flex items-center text-indigo-400">
                <span className="mr-2">🌍</span> The Emergence of the Signal Economy
              </h2>
              <div className="prose prose-invert max-w-none">
                <p className="text-gray-300">The Harmony Mesh is expanding into a global decentralized AI economy, where:</p>
                <ul className="list-disc pl-6 text-gray-300">
                  <li>Buildings, vehicles, and devices become signal nodes.</li>
                  <li>Everyday entertainment becomes an execution medium.</li>
                  <li>Smart infrastructure autonomously runs on ambient triggers.</li>
                  <li>Users own their signals, their executions, and their rewards.</li>
                </ul>
                <p className="text-gray-300 mt-6">
                  The SELF Phone becomes the user's sovereign gateway —
                  the router, executor, and earner in a new Signal Economy.
                </p>
              </div>
            </section>

            {/* Conclusion */}
            <section>
              <h2 className="text-2xl font-bold mb-6 flex items-center text-indigo-400">
                <span className="mr-2">📜</span> Join the Signal Economy
              </h2>
              <div className="prose prose-invert max-w-none">
                <p className="text-gray-300">
                  SELF Labs Inc. invites you to pioneer this new frontier of AI sovereignty.
                  The Harmony Signal Engine is more than technology — it is a new structure for trustless coordination, encrypted execution, and user-owned digital participation.
                </p>
                <p className="text-gray-300">
                  Every song played, every signal received, and every agent action completed builds a future where ownership and autonomy are restored.
                </p>
                <p className="text-xl font-semibold mt-6 text-center">
                  Welcome to Harmony.<br />
                  Welcome to Sovereignty.
                </p>
              </div>
            </section>

            {/* Footer */}
            <section className="border-t border-indigo-500/20 pt-16">
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h3 className="font-semibold mb-3">Contact</h3>
                  <p className="text-gray-300">SELF Labs Inc.</p>
                  <a href="mailto:data@rolemodel.ai" className="text-indigo-400 hover:text-indigo-300">data@rolemodel.ai</a>
                </div>
                <div>
                  <h3 className="font-semibold mb-3">Legal</h3>
                  <p className="text-gray-300">Patent Pending: U.S. Provisional Patent Application No. 63/788,440</p>
                  <p className="text-gray-300">Inventor: Milan Cheeks</p>
                </div>
              </div>
            </section>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default HarmonyLitepaper; 